import React, { useEffect, useMemo, useRef, useState } from 'react';
import { OverlayPanel } from 'primereact/overlaypanel';
import { Slider } from 'primereact/slider';
import { InputText } from 'primereact/inputtext';

/* Radio u zaglavlju.
   Srpski: proverene HTTPS adrese stanica (Radio S kanali, Naxi, Play, TDI) — bez API-ja, pa bez "pesma koja ide".
   Inostrani: SomaFM (nekomercijalni, bez reklama) — spisak, opis i pesma koja ide iz javnog API-ja api.somafm.com.
   Izbor kanala i jačina se pamte u pregledaču. Zvuk je jedan <audio> za celu aplikaciju, pa svira i pri promeni ekrana.
   index.html šalje Referer samo istom sajtu (SomaFM odbija strim sa Referer-om localhost-a). */

export const SRPSKI = [
    ['s1', 'Radio S1', 'pop · domaće i strano', 'https://stream.radios.rs:9000/;stream.nsv'],
    ['naxi', 'Naxi Radio', 'najbolja domaća muzika', 'https://naxi128ssl.streaming.rs:9152/;stream.nsv'],
    ['play', 'Play Radio', 'hitovi', 'https://stream.playradio.rs:8443/play.mp3'],
    ['tdi', 'TDI Radio', 'hot AC', 'https://streaming.tdiradio.com/tdiradio.mp3'],
    ['lola', 'Radio Lola', 'domaća muzika', 'https://streaming.tdiradio.com/radiolola.mp3'],
    ['smix', 'Radio S Mix', 'miks', 'https://stream.radios.rs:9010/;stream.nsv'],
    ['sgold', 'Radio S Gold', 'stari hitovi', 'https://stream.radios.rs:9020/;stream.nsv'],
    ['s2000', 'Radio S 2000-te Folk', 'folk 2000-ih', 'https://stream.radios.rs:9060/;stream.nsv'],
    ['skolo', 'Radio S Kolo', 'narodna · kolo', 'https://stream.radios.rs:9070/;stream.nsv'],
    ['szavicaj', 'Radio S Zavičaj', 'narodna', 'https://stream.radios.rs:9040/;stream.nsv'],
    ['seasy', 'Radio S Easy', 'lagana muzika', 'https://stream.radios.rs:9100/;stream.nsv'],
    ['strap', 'Radio S Trap & Rap', 'trep · rep', 'https://stream.radios.rs:9030/;stream.nsv'],
    ['tdihouse', 'TDI House Classics', 'house klasici', 'https://streaming.tdiradio.com/houseclassics.mp3'],
    ['ssport', 'Radio S Planeta Sport', 'sport', 'https://stream.radios.rs:9050/;stream.nsv'],
    ['surban', 'Radio S Sport Urban', 'sport · urban', 'https://stream.radios.rs:9080/;stream.nsv'],
].map(([id, title, genre, stream]) => ({ id, title, genre, stream, grupa: 'sr' }));

const API = 'https://api.somafm.com/channels.json';
const SOMA = (id, server = 'ice2') => `https://${server}.somafm.com/${id}-128-mp3`;
const SERVERI = ['ice2', 'ice1', 'ice4'];
/* preporučeni (opušteni) inostrani kanali na vrhu */
const PREPORUKA = ['groovesalad', 'lush', 'illstreet', 'secretagent', 'bossa', 'sonicuniverse', 'deepspaceone', 'seventies', 'folkfwd', '7soul'];
const ZANROVI = [
    { label: 'Sve', value: '' },
    { label: 'Chill', value: 'ambient' },
    { label: 'Lounge', value: 'lounge' },
    { label: 'Jazz', value: 'jazz' },
    { label: 'Elektronska', value: 'electronic' },
    { label: 'Rok/Pop', value: 'rock' },
];
const KLJUC = 'radio';

const procitaj = () => { try { return JSON.parse(localStorage.getItem(KLJUC)) || {}; } catch { return {}; } };
const sacuvaj = (v) => { try { localStorage.setItem(KLJUC, JSON.stringify(v)); } catch { /* privatni režim */ } };
const inicijali = (t) => t.replace(/^Radio\s+/i, '').split(/\s+/).slice(0, 2).map((w) => w[0]).join('').toUpperCase();

const zvuk = typeof Audio !== 'undefined' ? new Audio() : null;
if (zvuk) zvuk.preload = 'none';

/** logo kanala: slika (SomaFM) ili inicijali (srpske stanice) */
const Logo = ({ c, velik }) => (c && c.image
    ? <img src={c.image} alt="" loading="lazy" className={velik ? 'radio-logo velik' : 'radio-logo'} />
    : <span className={`radio-logo inicijali${velik ? ' velik' : ''}`} aria-hidden="true">{c ? inicijali(c.title) : <i className="bi bi-broadcast" />}</span>);

export default function RadioPlayer() {
    const op = useRef(null);
    const pamti = procitaj();
    const [soma, setSoma] = useState([]);
    const [greska, setGreska] = useState(null);
    const [kanal, setKanal] = useState(pamti.kanal || 's1');
    const [jacina, setJacina] = useState(pamti.jacina ?? 60);
    const [svira, setSvira] = useState(false);
    const [ucitava, setUcitava] = useState(false);
    const [grupa, setGrupa] = useState(pamti.grupa || 'sr');
    const [zanr, setZanr] = useState('');
    const [trazi, setTrazi] = useState('');
    const server = useRef(0);
    const kanalRef = useRef(kanal);
    kanalRef.current = kanal;

    const ucitajSoma = () => fetch(API).then((r) => r.json())
        .then((j) => setSoma((j.channels || []).map((c) => ({ ...c, grupa: 'int', stream: SOMA(c.id) }))))
        .catch(() => setGreska('Spisak inostranih kanala trenutno nije dostupan.'));

    const sviKanali = useMemo(() => [...SRPSKI, ...soma], [soma]);
    const trenutni = sviKanali.find((c) => c.id === kanal) || null;

    /* dok svira inostrani kanal, spisak se osvežava svakih 30 s (pesma koja trenutno ide) */
    useEffect(() => {
        if (!svira || !trenutni || trenutni.grupa !== 'int') return undefined;
        const t = setInterval(ucitajSoma, 30000);
        return () => clearInterval(t);
    }, [svira, trenutni]);

    useEffect(() => { if (zvuk) zvuk.volume = jacina / 100; sacuvaj({ kanal, jacina, grupa }); }, [kanal, jacina, grupa]);

    useEffect(() => {
        if (!zvuk) return undefined;
        const on = {
            playing: () => { setSvira(true); setUcitava(false); },
            pause: () => setSvira(false),
            waiting: () => setUcitava(true),
            error: () => {
                /* SomaFM: drugi server istog kanala, pa odustajanje */
                const k = kanalRef.current;
                const somaKanal = !SRPSKI.some((c) => c.id === k);
                if (somaKanal && server.current < SERVERI.length - 1 && zvuk.src) {
                    server.current += 1; zvuk.src = SOMA(k, SERVERI[server.current]); zvuk.play().catch(() => {}); return;
                }
                setUcitava(false); setSvira(false); setGreska('Kanal trenutno ne može da se pusti. Probajte drugi.');
            },
        };
        Object.entries(on).forEach(([k, f]) => zvuk.addEventListener(k, f));
        return () => Object.entries(on).forEach(([k, f]) => zvuk.removeEventListener(k, f));
    }, []);

    const pusti = (c = trenutni) => {
        if (!zvuk || !c) return;
        server.current = 0;
        setGreska(null);
        setKanal(c.id);
        setUcitava(true);
        zvuk.src = c.stream;
        zvuk.play().catch(() => setUcitava(false));
    };
    const zaustavi = () => { if (zvuk) { zvuk.pause(); zvuk.removeAttribute('src'); zvuk.load(); } setSvira(false); setUcitava(false); };
    const aktivan = svira || ucitava;

    const prikaz = useMemo(() => {
        const t = trazi.trim().toLowerCase();
        const izvor = grupa === 'sr' ? SRPSKI : soma.filter((c) => !zanr || (c.genre || '').includes(zanr))
            .sort((a, b) => {
                const pa = PREPORUKA.indexOf(a.id), pb = PREPORUKA.indexOf(b.id);
                return (pa < 0 ? 99 : pa) - (pb < 0 ? 99 : pb) || a.title.localeCompare(b.title);
            });
        return izvor.filter((c) => !t || `${c.title} ${c.genre} ${c.description || ''}`.toLowerCase().includes(t));
    }, [grupa, soma, zanr, trazi]);

    const opis = (c) => {
        if (!c) return 'Izaberite kanal';
        if (aktivan && c.lastPlaying) return `♪ ${c.lastPlaying}`;
        return c.description || c.genre;
    };

    return (
        <>
            <button type="button" className={`radio-btn${aktivan ? ' svira' : ''}`}
                onClick={(e) => { if (!soma.length) ucitajSoma(); op.current.toggle(e); }}
                title={aktivan ? `Radio: ${trenutni ? trenutni.title : ''}` : 'Radio — muzika uz rad'} aria-label="Radio" aria-haspopup="dialog">
                {aktivan
                    ? <span className={`radio-eq${ucitava ? ' ceka' : ''}`} aria-hidden="true"><i /><i /><i /><i /></span>
                    : <i className="bi bi-music-note-beamed" aria-hidden="true" />}
            </button>
            <OverlayPanel ref={op} className="radio-panel" aria-label="Radio">
                <div className="radio-head">
                    <div className="radio-sada">
                        <Logo c={trenutni} velik />
                        <div>
                            <b>{trenutni ? trenutni.title : 'Radio'}</b>
                            <span>{opis(trenutni)}</span>
                        </div>
                    </div>
                    <button type="button" className={`radio-play${aktivan ? ' svira' : ''}`} onClick={() => (aktivan ? zaustavi() : pusti())}
                        aria-label={aktivan ? 'Zaustavi radio' : 'Pusti radio'}>
                        <i className={`pi ${ucitava ? 'pi-spin pi-spinner' : svira ? 'pi-pause' : 'pi-play'}`} aria-hidden="true" />
                    </button>
                </div>
                <div className="radio-jacina">
                    <i className={`bi ${jacina === 0 ? 'bi-volume-mute' : jacina < 50 ? 'bi-volume-down' : 'bi-volume-up'}`} aria-hidden="true" />
                    <Slider value={jacina} onChange={(e) => setJacina(e.value)} aria-label="Jačina zvuka" style={{ flex: 1 }} />
                    <span>{jacina}%</span>
                </div>
                {greska && <div className="callout warn"><i className="pi pi-exclamation-triangle" /> {greska}</div>}
                <div className="radio-grupe" role="tablist" aria-label="Grupa kanala">
                    <button type="button" role="tab" aria-selected={grupa === 'sr'} className={grupa === 'sr' ? 'active' : ''} onClick={() => setGrupa('sr')}>Srpski</button>
                    <button type="button" role="tab" aria-selected={grupa === 'int'} className={grupa === 'int' ? 'active' : ''} onClick={() => setGrupa('int')}>Inostrani</button>
                </div>
                {grupa === 'int' && (
                    <div className="radio-filteri">
                        {ZANROVI.map((z) => (
                            <button key={z.value} type="button" className={`ai-chip${zanr === z.value ? ' active' : ''}`} onClick={() => setZanr(z.value)}>{z.label}</button>
                        ))}
                    </div>
                )}
                <span className="radio-trazi">
                    <InputText value={trazi} onChange={(e) => setTrazi(e.target.value)} placeholder="Traži kanal…" aria-label="Traži kanal" />
                </span>
                <ul className="radio-lista">
                    {grupa === 'int' && !soma.length && !greska && <li className="radio-prazno"><i className="pi pi-spin pi-spinner" /> Učitavanje kanala…</li>}
                    {prikaz.map((c) => (
                        <li key={c.id}>
                            <button type="button" className={`radio-kanal${c.id === kanal ? ' izabran' : ''}`} onClick={() => pusti(c)}>
                                <Logo c={c} />
                                <span>
                                    <b>{c.title}</b>
                                    <small>{(c.genre || '').replace(/\|/g, ' · ')}{c.listeners ? ` · ${c.listeners} slušalaca` : ''}</small>
                                </span>
                                {c.id === kanal && aktivan && <span className="radio-eq mali" aria-hidden="true"><i /><i /><i /></span>}
                            </button>
                        </li>
                    ))}
                </ul>
                <p className="radio-izvor">
                    {grupa === 'sr' ? 'Srpske stanice: javni strimovi stanica (Radio S, Naxi, Play, TDI).'
                        : <>Muzika: <a href="https://somafm.com" target="_blank" rel="noreferrer">SomaFM</a> — nekomercijalni radio bez reklama, finansiraju ga slušaoci.</>}
                </p>
            </OverlayPanel>
        </>
    );
}
